<template>
  <div class="center-middle overflow-container">
    <div class="loading-state" v-if="loading">
      <LoadingState :size="50" />
    </div>
    <div class="main-state" v-else>
      <Card title="Case Verification">
        <template v-if="clientId">
          <div>
            {{
              `Press the button below if you want to verify your case for ${parseClientName(
                currentClient.name,
                currentClient.id
              )}.`
            }}
          </div>
          <div class="form-buttons">
            <Btn
              variant="primary"
              type="submit"
              data-test="ticket-verify-confirm-button"
              :loading="isSubmitting"
              @click="validateTicket"
            >
              Submit
            </Btn>
            <Btn
              class="ms-3"
              variant="primary"
              type="submit"
              data-test="ticket-verify-back-button"
              :loading="isSubmitting"
              @click="backToClientSelect"
            >
              Back
            </Btn>
          </div>
        </template>
        <template v-else-if="hasSingleClient()">
          <div>Press the button below if you want to verify your case.</div>
          <div class="form-buttons">
            <Btn
              variant="primary"
              type="submit"
              data-test="ticket-verify-confirm-button"
              :loading="isSubmitting"
              @click="validateTicket"
            >
              Submit
            </Btn>
            <Btn
              class="ms-3"
              variant="primary"
              type="submit"
              data-test="ticket-verify-back-button"
              :loading="isSubmitting"
              @click="cancelVerify"
            >
              Cancel
            </Btn>
          </div>
        </template>
        <template v-else-if="hasMultipleClients()">
          <div class="mb-3">In order to verify your case, please select your client below.</div>
          <FormSelect
            name="client"
            v-model="fields.client"
            testId="client-input"
            label="Client Name *"
            :errors="validation.fields.client.errors"
            :dirty="validation.fields.client.dirty"
            :disabled="isSubmitting"
          >
            <option
              v-for="(item, index) in clients"
              :key="index"
              :value="item.id"
              :selected="isSelected(item)"
            >
              {{ item.identity }}
            </option>
          </FormSelect>
          <div class="text-end">
            <Btn
              class="text-end"
              variant="primary"
              data-test="client-select-button"
              :disabled="!isSubmittable()"
              :loading="isSubmitting"
              @click="switchClient"
            >
              Select
            </Btn>
            <Btn
              class="ms-3"
              variant="primary"
              type="submit"
              data-test="ticket-verify-cancel-button"
              :loading="isSubmitting"
              @click="cancelVerify"
            >
              Cancel
            </Btn>
          </div>
        </template>
        <template v-else> <div class="empty-card" /> </template>
      </Card>
    </div>
  </div>
</template>

<script>
import { computed, reactive, ref } from "vue";
import Btn from "@/components/display/Btn";
import Card from "@/components/display/Card";
import constants from "@/utils/constants";
import FormSelect from "@/components/display/form/FormSelect";
import LoadingState from "@/components/display/LoadingState";
import router from "@/router";
import store from "@/store";
import { switchClientAction } from "@/utils/switchClient";
import useValidator from "@/utils/useValidator";
import { validations as validationRules } from "leatherman-js";

export default {
  name: "VerifyTicket",
  components: {
    Btn,
    Card,
    LoadingState,
    FormSelect,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    clientId: Number,
  },
  setup(props) {
    const { validator } = useValidator();

    const clients = computed(() => store.getters["auth/getLoginableClients"]);
    const currentClient = computed(() => store.getters["auth/getClient"]);
    const currentClientId = currentClient.value?.id;

    const fields = reactive({ client: currentClientId });

    const isSubmitting = ref(false);
    const loading = ref(false);

    const isSubmittable = () => !isSubmitting.value && validation.errors.length === 0;
    const hasMultipleClients = () => clients.value?.length > 1;
    const hasSingleClient = () => clients.value?.length === 1;
    const backToClientSelect = () =>
      router.push({ name: constants.ROUTES.VERIFY_CASE, query: { id: props.id } });
    const cancelVerify = () => router.push({ name: constants.ROUTES.HOME });
    const isSelected = ({ id }) => id === fields.client;

    const validation = validator(
      fields,
      validationRules.forms.verifyTicket,
      {},
      {
        client: {
          required: "Please select a client.",
        },
      }
    );

    const switchClient = () => {
      const route = {
        name: constants.ROUTES.VERIFY_CASE,
        query: { id: props.id, client: fields.client },
      };

      if (fields.client === currentClientId) {
        router.push(route);
      } else {
        switchClientAction(fields.client, route);
      }
    };

    const validateTicket = () => {
      loading.value = true;
      store
        .dispatch("tickets/claim", {
          id: props.id,
        })
        .then(() => {
          store.dispatch("toasts/addToast", {
            variant: "info",
            message: "We are processing your case verification.",
            timeout: true,
          });
        })
        .catch(() => {
          store.dispatch("toasts/addToast", {
            variant: "error",
            message: "Something went wrong trying to verify your case.",
            timeout: true,
          });
        })
        .finally(() => {
          loading.value = false;
          router.push({ name: constants.ROUTES.CASES });
        });
    };

    const parseClientName = (name, id) => {
      return `${name} (${id})`;
    };

    return {
      backToClientSelect,
      cancelVerify,
      clients,
      currentClient,
      fields,
      hasMultipleClients,
      hasSingleClient,
      isSelected,
      isSubmittable,
      isSubmitting,
      loading,
      parseClientName,
      switchClient,
      validateTicket,
      validation,
    };
  },
};
</script>

<style lang="scss" scoped>
.form-buttons {
  text-align: right;
  padding-top: 2rem;
}
.empty-card {
  margin-left: 26rem;
  margin-top: 7rem;

  @media (max-width: 1200px) {
    margin-left: 15rem;
  }
}
</style>
